// src/pages/MainApp.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import '../App.css';
import { tryAutoDetect, resetAutoDetect } from '../utils/autoDetect';
import { captureSession, loadBaseline, saveBaseline } from '../utils/captureSession';
import { harToRows } from '../utils/harToRows';

import InspectraNetworkView, { ts } from '../components/InspectraNetworkView';
import StreamActivityMonitor from '../components/StreamActivityMonitor';
import WatchPanel from '../components/WatchPanel';
import { useTraffic } from '../context/TrafficContext';
import { splitByBoundary } from '../utils/sessionBoundary';
import { authHeader, handleAuthResponse, notifyAuthExpired } from '../api/authFetch';
import API_BASE from '../apiBase';

// Two-tier history retention + slim-snapshot helpers moved to utils/liveHistory.js
// and are now owned by the app-level live-traffic engine (context/TrafficContext.js).

// InspectraNetworkView and StreamActivityMonitor are NOT memoized in their own
// files (out of scope to edit here — see the off-dashboard perf fix below), so
// wrap them once, at module scope, with React.memo. A parent re-render alone
// (which happens on every WS event, since MainApp reads TrafficContext) always
// re-executes un-memoized children regardless of whether their props changed —
// memoizing here is what lets the "freeze props while hidden" strategy below
// actually skip InspectraNetworkView's per-row validator/sort/group work
// instead of merely making it operate on stale-but-identical data.
const MemoInspectraNetworkView = React.memo(InspectraNetworkView);
const MemoStreamActivityMonitor = React.memo(StreamActivityMonitor);

// Stable empty sentinels so a `foo[key] || {}` / `|| []` fallback doesn't
// itself defeat memoization by handing out a new object/array identity every
// render (React.memo / useMemo compare by reference).
const EMPTY_OBJECT = Object.freeze({});
const EMPTY_ARRAY = Object.freeze([]);

/**
 * nextCommittedGroupedResults(committed, latestGroupedResults, onDashboard)
 *
 * Pure decision for the off-dashboard render pause (see "Off-dashboard render
 * pause" below for the full story). `groupedResults` from TrafficContext gets
 * a new object reference on every accepted WS event; MainApp normally mirrors
 * it straight into the render tree, which cascades into InspectraNetworkView
 * re-running its validator pass + sort/filter/group over up to 3,000 rows on
 * every single event — even while the Dashboard subtree is `display:none`
 * because another page is active.
 *
 * While NOT on the dashboard this returns `committed` unchanged (the caller
 * skips the setState, so nothing downstream re-renders) instead of advancing
 * to `latestGroupedResults` — no data is lost, `latestGroupedResults` is
 * simply not mirrored into local state for a while; TrafficContext keeps the
 * full snapshot regardless. The moment `onDashboard` flips true this returns
 * `latestGroupedResults` (whatever the freshest value is at that instant), so
 * returning to the dashboard flushes everything that accumulated while away
 * in exactly one update — the table is immediately correct, never a stale
 * intermediate snapshot.
 */
export function nextCommittedGroupedResults(committed, latestGroupedResults, onDashboard) {
    return onDashboard ? latestGroupedResults : committed;
}

/**
 * computeEffectiveTab(groupedResultsSnapshot, selectedTab)
 *
 * Pure re-extraction of the "fall back to first available tab" rule (e.g.
 * after a page restore where the previously-selected device tab no longer
 * exists in the snapshot).
 */
export function computeEffectiveTab(groupedResultsSnapshot, selectedTab) {
    return groupedResultsSnapshot[selectedTab]
        ? selectedTab
        : (Object.keys(groupedResultsSnapshot)[0] || selectedTab);
}

const tryJson = (x) => {
    if (x == null) return "";
    if (typeof x === "object") return x;
    if (typeof x !== "string") return String(x);
    try { return JSON.parse(x); } catch { return x; }
};

const buildUrl = (e) => {
    if (e.url) return e.url;
    if (e.host && e.path) return `https://${e.host}${e.path}`;
    return e.path || "about:blank";
};

// live → viewer row
export const toNetworkRow = (e, cfg) => ({
    id: e.requestId || e.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    method: e.method || (e.requestBody ? "POST" : "GET"),
    url: buildUrl(e),
    // The proxy broadcasts the real HTTP status as `status` (proxyOnRequestHandler
    // requestDetails). Read it FIRST — the old code only checked statusCode/
    // responseStatus and otherwise inferred `responseBody ? 200 : 0`, so once a body
    // was stripped (two-tier retention / slim backup) a real 200 collapsed to 0/"?".
    // stripBodies/stripHeavy keep `.status`, so the true status is always available.
    // Ingested rows (adb logcat) never had a response: keep status null so the UI
    // shows "—", not 0 (which reads as a failed request through the proxy).
    status: e.status ?? e.statusCode ?? e.responseStatus ?? (e.captureSource ? null : (e.responseBody ? 200 : 0)),
    type: e.requestType || (e.isGAM ? "GAM" : e.isPrebid ? "PREBID" : "") || e.platform,
    requestType: e.requestType,
    startedAt: e.timestamp,
    // When the request LEFT the proxy (ms). `timestamp` above is when the response
    // finished for MITM rows; sorting/lifecycle matching keep using it, the slot
    // waterfall needs the real start. The proxy sends durationMs at top level.
    requestStartedAt: e.startedAt,
    durationMs: e.durationMs ?? e.timing?.totalMs,
    size: e.responseSize ?? (e.responseBody ? e.responseBody.length : undefined),

    requestHeaders: e.requestHeaders,
    responseHeaders: e.responseHeaders,
    requestBody: tryJson(e.requestBody),
    responseBody: tryJson(e.responseBody),
    // B8 (body-coverage-spec WP3): toNetworkRow used to drop requestBodyParsed
    // entirely, so posted/live rows disagreed with what checks/validators saw
    // on history rows. Copy it through, plus the WP1 truncation/wire/strip
    // fields additively — they may not exist yet on live rows, pass-through
    // only when present so we never invent falsy values for older proxies.
    requestBodyParsed: e.requestBodyParsed,
    requestBodyTruncated: e.requestBodyTruncated,
    responseBodyTruncated: e.responseBodyTruncated,
    requestBodyWire: e.requestBodyWire,
    responseBodyWire: e.responseBodyWire,
    responseBytes: e.responseBytes,
    _bodyStripped: e._bodyStripped,
    configOverridden: e.configOverridden,

    cookies: e.cookies,
    contentEncoding: e.contentEncoding,
    responseContentType: e.responseContentType,
    isBinary: e.isBinary,

    configAttributes: cfg || {},

    issues: Array.isArray(e.issues) ? e.issues : [],

    // validators read these:
    validData: e.validData || e.serverReport || null,
    serverReport: e.validData || e.serverReport || null,
    contextRef: e.contextRef || null,

    // lifecycle / content opportunity signal (from proxy)
    contentOpportunity: e.contentOpportunity || null,
    winner: e.winner || null,
    step: e.step || undefined,  // app-flow step marker (per-tab attribution)
    // 'logcat' etc. for rows that came from POST /proxy/ingest/rows. Dropping it
    // made adb rows indistinguishable from MITM rows whose response failed.
    captureSource: e.captureSource || undefined,
    // proxy URL-rewrite record ({changes:[...]}); Slot Story reads it off the live row
    iuRewrite: e.iuRewrite,
    // logcat URL cut at ~4KB (logcatTap.js) — carried so exports keep it (Contract E)
    // the proxy reports the cut as an issue code, not a field (proxyOnRequestHandler ingest path)
    truncated: e.truncated || (Array.isArray(e.issues) && e.issues.some(i => i && i.code === 'ingest_url_truncated')) || undefined,
    // device platform for per-platform splits (bidHealth platformOf, Slots overview)
    platform: e.platform,
    devicePlatform: e.devicePlatform,
    platformClass: e.platformClass,
    deviceId: e.deviceId,
});

// HAR → viewer row conversion (kvToObj, extractReqBodyFromHar,
// extractRespBodyFromHar, harEntryToRow, harToRows) moved to
// ../utils/harToRows.js (WP3 of docs/capture-scoped-ask-spec.md) so the
// server-store analyst path can reuse the same converter instead of a second
// parallel one. `harToRows(har)` now returns
// `{ rows, entryCount, skipped, bodiesRetained }` — see the call site below.

/* =========================================================
   Main
   ========================================================= */
/* -------------------- Auto-detect banner UI --------------------
   Module scope on purpose: defined inside MainApp it was a NEW component type on
   every render, so React unmounted/remounted it (and its DOM) each time. */
function AutoDetectBanner({ autoDetectedBanner, initMode, captureSummary, pendingChanges, handleAcceptChange, setPendingChanges, setInitMode, setCaptureSummary, setAutoDetectedBanner, handleSaveCapture, handleStopCapture, handleStartCapture, toastVisible, setToastVisible }) {
    if (!autoDetectedBanner) return null;

    const b = autoDetectedBanner;
    const hasBaseline = !!loadBaseline(b.appId, b.profileId);

    // ── Review mode ─────────────────────────────────────────────────────
    if (initMode === 'review' && captureSummary) {
        const s = captureSummary;
        const hasChanges = pendingChanges.length > 0;
        return (
            <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(0,0,0,0.55)', zIndex: 9999,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                <div style={{
                    background: 'var(--card)', borderRadius: 12, padding: 24,
                    maxWidth: 600, width: '90%', maxHeight: '80vh', overflowY: 'auto',
                    boxShadow: '0 8px 40px rgba(0,0,0,0.3)',
                }}>
                    <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 4 }}>
                        {hasChanges ? '⚠️ Config changes detected' : '✅ Capture complete'}
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
                        {b.appName} — {b.profileName} · {s.reqCount} requests · {s.iuRows.length} ad units
                    </div>

                    {/* Changes to accept/reject */}
                    {hasChanges && (
                        <div style={{ marginBottom: 16 }}>
                            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Review changes vs. saved baseline:</div>
                            {pendingChanges.map((c, i) => (
                                <div key={i} style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    padding: '7px 10px', borderRadius: 7, border: '1px solid var(--line)',
                                    background: 'var(--bg)', marginBottom: 6, fontSize: 12,
                                }}>
                                    <span style={{ fontFamily: c.type.includes('prebid') || c.type.includes('amazon') ? 'monospace' : 'inherit' }}>
                                        {c.label}
                                    </span>
                                    <div style={{ display: 'flex', gap: 6, flexShrink: 0, marginLeft: 12 }}>
                                        <button onClick={() => handleAcceptChange(c)} style={{ fontSize: 11, padding: '2px 10px', borderRadius: 5, border: 'none', background: '#1a6f3c', color: '#fff', cursor: 'pointer' }}>Accept</button>
                                        <button onClick={() => setPendingChanges(prev => prev.filter((_, j) => j !== i))} style={{ fontSize: 11, padding: '2px 10px', borderRadius: 5, border: '1px solid var(--line)', background: 'transparent', color: 'var(--muted)', cursor: 'pointer' }}>Reject</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Capture summary */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                        {[
                            ['Ad Units', s.iuRows.length],
                            ['Banner sizes', s.allSizes.join(', ') || '—'],
                            ['Publisher ID', s.publisherIds[0] || '—'],
                            ['Bundle ID', s.bundleIds[0] || '—'],
                            ['Prebid mappings', Object.keys(s.prebidMappings).length],
                            ['Amazon slots', Object.keys(s.amazonSlots).length],
                            ['Bidders seen', s.bidders.join(', ') || '—'],
                        ].map(([label, val]) => (
                            <div key={label} style={{ background: 'var(--bg)', borderRadius: 7, padding: '8px 10px', border: '1px solid var(--line)' }}>
                                <div style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase', marginBottom: 3 }}>{label}</div>
                                <div style={{ fontSize: 12, fontFamily: typeof val === 'number' ? 'inherit' : 'inherit', color: 'var(--ink)', fontWeight: 600 }}>{val}</div>
                            </div>
                        ))}
                    </div>

                    {s.iuRows.length > 0 && (
                        <div style={{ marginBottom: 14 }}>
                            <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>Ad units captured:</div>
                            <div style={{ background: 'var(--bg)', borderRadius: 7, padding: 10, border: '1px solid var(--line)', fontSize: 11, fontFamily: 'monospace', maxHeight: 120, overflowY: 'auto' }}>
                                {s.iuRows.map(r => <div key={r.iu} style={{ color: 'var(--ink)', marginBottom: 2 }}>{r.iu}</div>)}
                            </div>
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button onClick={() => { setInitMode('idle'); setCaptureSummary(null); setPendingChanges([]); setAutoDetectedBanner(null); }}
                            style={{ fontSize: 12, padding: '6px 14px', borderRadius: 7, border: '1px solid var(--line)', background: 'transparent', color: 'var(--muted)', cursor: 'pointer' }}>
                            Discard
                        </button>
                        <button onClick={() => handleSaveCapture(false)}
                            style={{ fontSize: 12, padding: '6px 18px', borderRadius: 7, border: 'none', background: 'var(--primary)', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
                            Save to {b.profileName} profile
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ── Capturing mode — show whenever captureSession is active (started from
    //    either the AutoDetectBanner toast OR the ConfigPage wizard) ────────
    if (initMode === 'capturing' || captureSession.isActive) {
        const csName = captureSession.appName && captureSession.profileName
            ? `${captureSession.appName} — ${captureSession.profileName}`
            : (autoDetectedBanner ? `${autoDetectedBanner.appName} — ${autoDetectedBanner.profileName}` : 'Capturing…');
        return (
            <div style={{
                position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)',
                background: '#1a1a2e', color: '#fff', borderRadius: 10,
                padding: '12px 20px', zIndex: 9000, boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                display: 'flex', alignItems: 'center', gap: 14, fontSize: 13, minWidth: 360,
            }}>
                <span style={{ fontSize: 16 }}>🔴</span>
                <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700 }}>🔴 {csName} — scroll through every page layout</div>
                    <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>
                        {captureSession.getSummary().iuRows.length} ad units · {captureSession.getSummary().allSizes.length} sizes · {Object.keys(captureSession.getSummary().prebidMappings).length} Prebid IDs
                    </div>
                </div>
                <button onClick={handleStopCapture}
                    style={{ fontSize: 12, padding: '5px 14px', borderRadius: 7, border: 'none', background: 'var(--card)', color: 'var(--ink)', fontWeight: 700, cursor: 'pointer' }}>
                    Done — Review
                </button>
            </div>
        );
    }

    // ── Toast / Init prompt (idle, just detected) ────────────────────────
    if (!toastVisible) return null;
    return (
        <div style={{
            position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)',
            background: 'var(--card)', borderRadius: 10, padding: '12px 16px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.25)', border: '1px solid var(--primary)',
            zIndex: 9000, display: 'flex', alignItems: 'center', gap: 12,
            minWidth: 360, maxWidth: 520, fontSize: 13,
        }}>
            <span style={{ fontSize: 20 }}>📡</span>
            <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, color: 'var(--ink)' }}>
                    Auto-detected: {b.appName} — {b.profileName}
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                    {hasBaseline
                        ? 'Comparing live traffic against saved baseline…'
                        : 'No baseline saved yet. Scroll through every layout to capture the config.'}
                </div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                {!hasBaseline && (
                    <button onClick={handleStartCapture} style={{
                        fontSize: 12, padding: '5px 14px', borderRadius: 7, border: 'none',
                        background: 'var(--primary)', color: '#fff', fontWeight: 700, cursor: 'pointer',
                    }}>
                        Initialize
                    </button>
                )}
                <button onClick={() => setToastVisible(false)} style={{
                    fontSize: 12, padding: '5px 10px', borderRadius: 7,
                    border: '1px solid var(--line)', background: 'transparent',
                    color: 'var(--muted)', cursor: 'pointer',
                }}>
                    ✕
                </button>
            </div>
        </div>
    );
}

function MainApp({ onDashboard = true } = {}) {
    // ---- Theme State ----
    // (Theme state moved to App.js)
    // ---- Live state ----
    // Request-type filter driven by clicking a chip in StreamActivityMonitor
    const [typeFilter, setTypeFilter] = useState(null);

    // MainApp is mounted OUTSIDE <Routes> (App.js) so the live WebSocket in
    // TrafficContext survives navigation, and is merely CSS-hidden
    // (display:'contents' vs 'none') when another page is active.
    //
    // Visibility is threaded in as a prop from App.js (`onDashboard={onDashboard}`
    // at the `<MainApp />` call site) rather than derived locally via
    // `useLocation()`. Tried useLocation first — App.js already computes the
    // identical `location.pathname === '/'` check for its own wrapper div, so
    // reusing it looked like the simpler, single-source-of-truth option. It
    // broke the test harness: react-router-dom v7 is ESM-only with an
    // `exports` map this project's Jest (react-scripts 5.0.1 / Jest 27) can't
    // resolve — `import { useLocation } from 'react-router-dom'` inside
    // MainApp.js makes `Cannot find module 'react-router-dom'` fail the whole
    // suite the instant anything imports MainApp.js (confirmed: no existing
    // test in this project imports react-router-dom, directly or
    // transitively, today). App.js already pays that cost as the app's route
    // root and has a passing story for it; MainApp.js/its tests don't need to.
    // A one-line prop avoids the dependency entirely. Defaults to `true` (the
    // pre-existing always-live behavior) so any other caller that renders
    // `<MainApp />` without the prop — a future test, most likely — is
    // unaffected.

    // The single live socket + captured-row state now live in the app-level engine
    // (context/TrafficContext.js), mounted once above the router — so capture keeps
    // running across route changes. The Dashboard CONSUMES that state here instead of
    // owning it. `liveTrafficGroups`/`wsStatus` keep their old local names below so the
    // rest of this component reads verbatim.
    const {
        liveTrafficGroups: groupedResults,
        wsStatus: wsConnectState,
        serverIp,
        tlsDrops,
        watchAlerts, dismissWatchAlert, clearWatchAlerts,
        deviceConfig,
        pubSettings,
        pausedDevices,
        suppressedByDevice,
        selectedTab, setSelectedTab,
        togglePauseDevice,
        wsSend,
        storageNotice, dismissStorageNotice,
        subscribeMessages,
        sessionBoundary,
    } = useTraffic();

    // "New session" boundary — collapses rows captured before the last stamp
    // behind a Show/Hide banner instead of deleting them. NOT persisted: a fresh
    // boundary always starts collapsed, even across a reload (loadBoundary() only
    // restores the timestamp itself, not the UI's expand/collapse choice).
    const [showEarlier, setShowEarlier] = useState(false);

    // ── Auto-detect / Init mode state ────────────────────────────────────────
    // null = idle  |  { appId, profileId, appName, profileName } = banner showing
    const [autoDetectedBanner, setAutoDetectedBanner] = useState(null);
    // 'idle' | 'capturing' | 'review'
    const [initMode, setInitMode] = useState('idle');
    const [captureSummary, setCaptureSummary] = useState(null);
    // pending changes from comparing live session to saved baseline
    const [pendingChanges, setPendingChanges] = useState([]);
    // used to dismiss the auto-detect toast independently from the init prompt
    const [toastVisible, setToastVisible] = useState(false);
    const toastTimerRef = useRef(null);
    // ─────────────────────────────────────────────────────────────────────────

    // ── Dashboard-owned reactions to live traffic ────────────────────────────
    // The engine owns the socket + row ingestion, but these reactions touch
    // Dashboard-local UI state (auto-detect toast + capture session), so they stay
    // here and subscribe to the engine's message stream. The engine calls this for a
    // full history_cleared and for accepted auction events (paused-dropped events are
    // NOT delivered), preserving the original inline order/semantics that used to
    // live in the WS handler.
    const handleEngineMessage = useCallback((data, newEntry) => {
        if (data?.type === 'history_cleared' && !data.deviceId) {
            // Reset auto-detect so the next session can re-detect
            resetAutoDetect();
            setAutoDetectedBanner(null);
            setInitMode('idle');
            setCaptureSummary(null);
            setPendingChanges([]);
            captureSession.stop();
            return;
        }
        if (newEntry) {
            // ── Auto-detect app + platform from GAM requests ──────────────────
            if (newEntry.requestType === 'google') {
                const switched = tryAutoDetect(newEntry);
                if (switched) {
                    // Show init banner for first-time detection
                    setAutoDetectedBanner(switched);
                    setToastVisible(true);
                    clearTimeout(toastTimerRef.current);
                    // Auto-dismiss toast after 8s if user doesn't interact
                    toastTimerRef.current = setTimeout(() => setToastVisible(false), 8000);
                }
            }
            // ── Feed capture session whenever it's active ─────────────────────
            // captureSession.isActive is set by the ConfigPage wizard or the
            // AutoDetectBanner — no local initMode state needed here.
            if (captureSession.isActive) {
                captureSession.addEntry(newEntry);
            }
        }
    }, []);
    useEffect(() => subscribeMessages(handleEngineMessage), [subscribeMessages, handleEngineMessage]);

    // ---- Session tabs (Live + HARs) ----
    const [sessionTabs, setSessionTabs] = useState([
        { key: 'live', title: 'Live', kind: 'live' }
    ]);
    const [activeSessionKey, setActiveSessionKey] = useState('live');
    const [harSessions, setHarSessions] = useState({}); // id -> { rows, meta, title }

    // expose opener for AdminPanel (optional)
    // Registered once, so it must read the CURRENT openHarById/sessionTabs through a
    // ref — a closure over the first render's `sessionTabs` never sees later tabs and
    // opened a duplicate tab for a HAR that was already open.
    const openHarByIdRef = useRef(null);
    openHarByIdRef.current = openHarById;
    useEffect(() => {
        window.openHarSession = async (id) => { await openHarByIdRef.current(id); };
    }, []);

    const [slotLink, setSlotLink] = useState(null); // ?slot= deep link -> InspectraNetworkView

    // pick up ?har= or ?device= on first load
    useEffect(() => {
        const sp = new URLSearchParams(window.location.search);

        // Handle HAR loading
        const harId = sp.get('har');
        if (harId) {
            openHarById(harId).finally(() => {
                sp.delete('har');
                const q = sp.toString();
                window.history.replaceState({}, '', window.location.pathname + (q ? `?${q}` : ''));
            });
        }

        // Handle Device selection (from Admin Panel connect)
        const deviceId = sp.get('device');
        if (deviceId) {
            setSelectedTab(deviceId); // Set the tab immediately (engine's setSelectedTab syncs its WS ref)
            // Optional: clean up URL
            sp.delete('device');
            const q = sp.toString();
            window.history.replaceState({}, '', window.location.pathname + (q ? `?${q}` : ''));
        }

        // Handle a Slot Story deep link (?slot=<key>[&device=][&har=]). The slot param is
        // NOT stripped here: InspectraNetworkView/SlotStoryView call onSlotLinkDone once the
        // slot actually resolved, so an unresolved link keeps its URL and shows the banner.
        const slotKey = sp.get('slot');
        if (slotKey) setSlotLink({ key: slotKey, at: Date.now() });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const handleSlotLinkDone = useCallback(() => {
        try {
            const sp = new URLSearchParams(window.location.search);
            if (!sp.has('slot')) return;
            sp.delete('slot');
            const q = sp.toString();
            window.history.replaceState({}, '', window.location.pathname + (q ? `?${q}` : ''));
        } catch { /* URL cleanup is cosmetic */ }
    }, []);

    // One-line, dismissible notice (replaces the blocking alert()). Fed by the reload
    // guard below and by the context's persist-quota notice.
    const [storageNoticeLocal, setStorageNoticeLocal] = useState('');
    const shownStorageNotice = storageNoticeLocal || storageNotice || '';
    const closeStorageNotice = () => { setStorageNoticeLocal(''); if (dismissStorageNotice) dismissStorageNotice(); };

    // ---- Light guard for hard refresh info ----
    useEffect(() => {
        if (performance?.navigation?.type === 1) {
            console.warn('🔄 Page was hard reloaded!');
            if (!localStorage.getItem('groupedResults.slim')) {
                // Non-blocking: a modal alert() froze the dashboard on every reload.
                setStorageNoticeLocal('Auction data cleared due to reload or memory purge.');
            }
        }
    }, []);

    // ---- Off-dashboard render pause ----
    // Profiled: `groupedResults` (TrafficContext) gets a new object reference
    // on every accepted WS event. This component used to read it directly, so
    // every event forced a fresh `currentGroup.map(toNetworkRow)` PLUS handed
    // InspectraNetworkView (and StreamActivityMonitor) a brand-new `data`/`rows`
    // array reference. InspectraNetworkView is not memoized, and even wrapped
    // in React.memo a changed array reference defeats it — inside it, the
    // `validationIndex` useMemo (components/InspectraNetworkView.jsx ~line 2093)
    // runs the full validator engine (`runValidations`) over EVERY row on every
    // such change, and the `rows` useMemo (~line 2139) re-maps/filters/sorts
    // every row too. Those two — not the sort in `groups`/`allRowsStamped`, not
    // StreamActivityMonitor's own O(n) bucket pass — are what dominate: a full
    // validator run per row, per WS event, for up to 3,000 rows, whether or not
    // anyone can see the result. WS capture itself (TrafficContext) is
    // untouched here and keeps ingesting every event — only the downstream
    // re-derive/re-render work below is paused.
    //
    // `committedGroupedResults` is what the render tree actually consumes.
    // `groupedResults` (TrafficContext) already IS the full up-to-3,000-row
    // snapshot dictionary every render, not an incremental delta — so "not
    // syncing" while hidden never drops anything, it just means the local
    // mirror stops tracking it for a while.
    //
    // This resync is done DURING render (React's documented "adjusting state
    // when a prop/external value changes" pattern — condition + setState in
    // the render body, not inside a useEffect): when `onDashboard` is true and
    // the mirror is stale, React re-renders immediately with the fresh value
    // BEFORE committing to the DOM, so the dashboard has zero added latency
    // while actively visible. A useEffect-based resync was tried first and
    // rejected — it commits the STALE value to the screen for one extra
    // render/paint before catching up on the next tick, which is a visible
    // regression on the actively-watched dashboard, exactly the case that must
    // stay instant. While off-dashboard, the guard below simply never fires,
    // so `committedGroupedResults` sits frozen at one stable reference — this
    // IS the "buffer" (no per-event setState) — for the entire hidden period,
    // however many WS events arrive. The very first render back on the
    // dashboard (`onDashboard` flips true) hits the guard and flushes the
    // CURRENT `groupedResults` — always the freshest value, since
    // TrafficContext never stopped ingesting — in that one `setState` call.
    const [committedGroupedResults, setCommittedGroupedResults] = useState(groupedResults);
    const nextCommitted = nextCommittedGroupedResults(committedGroupedResults, groupedResults, onDashboard);
    if (nextCommitted !== committedGroupedResults) {
        setCommittedGroupedResults(nextCommitted);
    }

    // ---- Live rows ----
    // If selectedTab no longer matches any key (e.g. after page restore), fall back to first available tab
    const effectiveTab = computeEffectiveTab(committedGroupedResults, selectedTab);
    const currentGroup = useMemo(
        () => committedGroupedResults[effectiveTab] || EMPTY_ARRAY,
        [committedGroupedResults, effectiveTab]
    );
    const cfgForThisDevice = deviceConfig[effectiveTab] || EMPTY_OBJECT;
    const liveRows = useMemo(
        () => currentGroup.map(e => toNetworkRow(e, cfgForThisDevice)),
        [currentGroup, cfgForThisDevice]
    );

    /* -------------------- HAR loading -------------------- */
    function authFetchHeaders() {
        return authHeader();
    }

    // fetch wrapper: consume any rotated token (sliding session) and route a 401
    // through the shared graceful re-auth flow instead of a hard failure.
    async function authFetch(url, opts) {
        const res = await fetch(url, opts);
        handleAuthResponse(res);
        if (res.status === 401) notifyAuthExpired();
        return res;
    }

    async function openHarById(id) {
        // focus if already open
        const exists = sessionTabs.find(t => t.key === id);
        if (exists) { setActiveSessionKey(id); return; }

        // meta for nice title
        let meta = null;
        try {
            const r = await authFetch(`${API_BASE}/api/har/${encodeURIComponent(id)}/meta`, { headers: authFetchHeaders() });
            if (r.ok) meta = await r.json();
        } catch { }
        const created = meta?.createdAt ? new Date(meta.createdAt) : null;
        const nice = created ? created.toLocaleString() : new Date().toLocaleString();
        const title = `${meta?.host || meta?.pageHost || 'Session'} • ${nice}`;

        // HAR itself
        const res = await authFetch(`${API_BASE}/api/har/${encodeURIComponent(id)}`, { headers: authFetchHeaders() });
        if (!res.ok) { if (res.status !== 401) alert('Failed to load HAR'); return; }
        const har = await res.json();
        const { rows } = harToRows(har);

        setHarSessions(s => ({ ...s, [id]: { rows, meta, title } }));
        setSessionTabs(t => [...t, { key: id, title, kind: 'har' }]);
        setActiveSessionKey(id);
    }

    function closeHarTab(id) {
        setSessionTabs(t => t.filter(x => x.key !== id));
        setHarSessions(s => {
            const n = { ...s };
            delete n[id];
            return n;
        });
        if (activeSessionKey === id) setActiveSessionKey('live');
    }

    /* -------------------- Which rows to show -------------------- */
    const viewingLive = activeSessionKey === 'live';
    const rawDisplayRows = viewingLive ? liveRows : (harSessions[activeSessionKey]?.rows || EMPTY_ARRAY);

    // Session boundary is a live-capture concept only — a loaded HAR is already a
    // fixed, completed session, so it always shows in full regardless of the boundary.
    // Guard against a boundary in the future (clock skew / stale test data left in
    // localStorage from a prior run) by treating it as unset rather than hiding
    // every row.
    const effectiveBoundary = (viewingLive && sessionBoundary != null && sessionBoundary <= Date.now())
        ? sessionBoundary
        : null;

    // Split happens at the TOP of the row pipeline — this feeds InspectraNetworkView's
    // `data` prop, which both the display-filtered `rows` AND the unfiltered `allRowsStamped`
    // (passed to AdLifecycleView) derive from — so an old session's rows never pollute a
    // new session's lifecycle assembly, FAIL/warn counts, request-count chip, or exports.
    // Memoized so its reference (and the `displayRows` derived from it below) stays
    // stable whenever `rawDisplayRows`/`effectiveBoundary` haven't actually changed —
    // required for the MemoInspectraNetworkView/MemoStreamActivityMonitor `data`/`rows`
    // props to stay referentially frozen while off-dashboard (see above).
    const boundarySplit = useMemo(
        () => (effectiveBoundary ? splitByBoundary(rawDisplayRows, effectiveBoundary, ts) : { current: rawDisplayRows, earlier: EMPTY_ARRAY }),
        [rawDisplayRows, effectiveBoundary]
    );
    const currentSessionRows = boundarySplit.current;
    const earlierSessionRows = boundarySplit.earlier;
    const displayRows = showEarlier ? rawDisplayRows : currentSessionRows;

    // Every device's rows, for the Slots view's "All devices" scope (iOS and
    // Android validated side by side). Live only, and only when there is more
    // than one device: a HAR is one capture, and one device needs no scope.
    // Each row carries its device tab so lifecycles are built per device:
    // impressions/bills are matched by timing, and two phones' traffic in one
    // build would cross-attribute.
    const allDevicesRows = useMemo(() => {
        const keys = Object.keys(committedGroupedResults);
        if (!viewingLive || keys.length < 2) return null;
        const out = [];
        for (const k of keys) {
            const cfg = deviceConfig[k] || EMPTY_OBJECT;
            for (const e of committedGroupedResults[k] || EMPTY_ARRAY) {
                const row = toNetworkRow(e, cfg);
                row._deviceTab = k;
                out.push(row);
            }
        }
        if (showEarlier || !effectiveBoundary) return out;
        return splitByBoundary(out, effectiveBoundary, ts).current;
    }, [committedGroupedResults, deviceConfig, viewingLive, showEarlier, effectiveBoundary]);

    /* -------------------- Init mode helpers -------------------- */
    function handleStartCapture() {
        const b = autoDetectedBanner;
        if (!b) return;
        captureSession.start(b.appId, b.profileId);
        setInitMode('capturing');
        clearTimeout(toastTimerRef.current);
    }

    function handleStopCapture() {
        captureSession.stop();
        const summary = captureSession.getSummary();
        setCaptureSummary(summary);

        // Compare against saved baseline for change detection
        const baseline = loadBaseline(summary.appId, summary.profileId);
        if (baseline) {
            const changes = captureSession.compareToBaseline(baseline);
            setPendingChanges(changes);
        }
        setInitMode('review');
    }

    function handleSaveCapture(overwrite = false) {
        captureSession.start(captureSummary.appId, captureSummary.profileId); // restore state
        // Re-populate state from summary (since captureSession was stopped)
        captureSession.stop();
        // Save directly from summary
        const { appId, profileId } = captureSummary;
        const { updateProfileField: upf } = require('../config/appProfiles');
        const compositeId = `${appId}/${profileId}`;
        const updates = {};
        if (captureSummary.publisherIds?.[0]) updates.publisherId = captureSummary.publisherIds[0];
        if (captureSummary.bundleIds?.[0])    updates.bundleId    = captureSummary.bundleIds[0];
        if (captureSummary.allSizes?.length)  updates.allowedBannerSizes = captureSummary.allSizes;
        if (Object.keys(captureSummary.prebidMappings || {}).length) {
            updates.prebidMappings = captureSummary.prebidMappings;
        }
        if (Object.keys(captureSummary.amazonSlots || {}).length) {
            const slots = {};
            Object.entries(captureSummary.amazonSlots).forEach(([uuid, d]) => {
                slots[uuid] = { name: uuid.slice(0, 8) + '…', sizes: d.sizes || [] };
            });
            updates.amazonSlotIds = slots;
        }
        if (Object.keys(updates).length) upf(compositeId, updates);
        saveBaseline(appId, profileId, captureSummary);
        setInitMode('idle');
        setCaptureSummary(null);
        setPendingChanges([]);
        setAutoDetectedBanner(null);
    }

    function handleAcceptChange(change) {
        // Accept a single change — mark it resolved
        setPendingChanges(prev => prev.filter(c => c !== change));
        // Update baseline entry for this change
        if (captureSummary) {
            const baseline = loadBaseline(captureSummary.appId, captureSummary.profileId) || {};
            if (change.type === 'new_ad_unit') {
                baseline.adUnits = [...new Set([...(baseline.adUnits || []), change.iu])];
            } else if (change.type === 'new_prebid_mapping' || change.type === 'changed_prebid_id') {
                baseline.prebidMappings = { ...(baseline.prebidMappings || {}), [change.key]: change.newId || change.id };
            } else if (change.type === 'new_amazon_slot') {
                baseline.amazonSlots = { ...(baseline.amazonSlots || {}), [change.uuid]: { sizes: [] } };
            } else if (change.type === 'new_size') {
                baseline.allSizes = [...new Set([...(baseline.allSizes || []), change.size])];
            }
            saveBaseline(captureSummary.appId, captureSummary.profileId, baseline);
        }
    }


    // Flatten groupedResults ({ [deviceId]: rows[] }) into one array for the
    // activity monitor's rolling-window computation across all devices.

    // ---- Stabilized props for MemoInspectraNetworkView ----
    // React.memo does a shallow prop comparison; an inline `Object.values(...)`,
    // `{}`/`[]` fallback, or arrow function passed as a prop is a NEW reference
    // every render, which defeats the memoization above just as surely as
    // passing new `data` would. Each of these must stay referentially stable
    // whenever its real inputs haven't changed, both so the off-dashboard
    // freeze actually holds AND so InspectraNetworkView doesn't do its
    // internal work again on the dashboard's normal (visible) re-renders
    // either — e.g. a WatchPanel alert firing shouldn't re-run the validator
    // pass over every row.
    const tlsDropsArray = useMemo(() => Object.values(tlsDrops), [tlsDrops]);
    const handleTogglePause = useCallback(() => togglePauseDevice(selectedTab), [togglePauseDevice, selectedTab]);
    const configAttributesGlobal = useMemo(
        () => (viewingLive ? (deviceConfig[selectedTab] || EMPTY_OBJECT) : (harSessions[activeSessionKey]?.meta?.configAttributes || EMPTY_OBJECT)),
        [viewingLive, deviceConfig, selectedTab, harSessions, activeSessionKey]
    );
    const publisherSettingsForView = useMemo(
        () => (viewingLive ? (pubSettings[selectedTab] || null) : (harSessions[activeSessionKey]?.meta?.publisherSettings || null)),
        [viewingLive, pubSettings, selectedTab, harSessions, activeSessionKey]
    );
    const handleClearLive = useCallback(() => {
        const plat = effectiveTab;
        if (!plat) return;
        if (window.confirm(`Clear all captured traffic for "${plat}"? This cannot be undone.`)) {
            // Scoped to the selected platform tab — server drops only this
            // device's history and broadcasts a scoped history_cleared.
            // The clear rides the live WS; while it is reconnecting (proxy
            // restart/deploy) the frame can't be sent, and a silent no-op
            // reads as "Clear is broken". Say so instead.
            if (!wsSend({ type: 'clear_history', deviceId: plat })) {
                window.alert('Not connected to the proxy right now (reconnecting), so nothing was cleared. Try again once the "Connection lost" banner is gone.');
            }
        }
    }, [effectiveTab, wsSend]);
    const handleRowOpen = useCallback((row) => {
        // Debug logging removed
    }, []);

    return (
        <div className="main-wrapper">
            {/* Auto-detect / Init mode banner */}
            {shownStorageNotice && (
                <div role="status" style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '6px 12px', margin: '0 0 8px',
                    background: 'var(--warn-bg)', color: 'var(--ink)', border: '1px solid var(--line)',
                    borderRadius: 8, fontSize: 12,
                }}>
                    <span style={{ flex: 1 }}>{shownStorageNotice}</span>
                    <button onClick={closeStorageNotice} aria-label="Dismiss notice"
                        style={{ background: 'transparent', border: 'none', color: 'var(--muted)', cursor: 'pointer' }}>✕</button>
                </div>
            )}
            <AutoDetectBanner {...{autoDetectedBanner, initMode, captureSummary, pendingChanges, handleAcceptChange, setPendingChanges, setInitMode, setCaptureSummary, setAutoDetectedBanner, handleSaveCapture, handleStopCapture, handleStartCapture, toastVisible, setToastVisible}} />

            {/* WS connection banner is now rendered globally by ProxyRibbon in App.js
                and visible on all tabs. Spacer below preserves layout when banner shows. */}
            {/* Spacer so content isn't hidden behind fixed banner */}
            {wsConnectState !== 'connected' && <div style={{ height: 38 }} />}

            {/* Session tabs — only shown when HAR sessions are open alongside Live */}
            {sessionTabs.length > 1 && (
                <div className="session-tabs">
                    {sessionTabs.map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveSessionKey(tab.key)}
                            title={tab.title}
                            className={activeSessionKey === tab.key ? 'chip chip--active' : 'chip'}
                        >
                            {tab.title}
                            {tab.kind === 'har' && (
                                <span
                                    onClick={(e) => { e.stopPropagation(); closeHarTab(tab.key); }}
                                    style={{ marginLeft: 8, opacity: 0.7 }}
                                >
                                    ✕
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            )}

            {/* Compact header strip — device tabs only (Clear moved into toolbar).
                Reads committedGroupedResults (the frozen-while-hidden mirror), not
                groupedResults directly — this whole subtree is display:none off-
                dashboard anyway, so there's no user watching these counts update. */}
            {viewingLive && Object.keys(committedGroupedResults).length > 1 && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                    <div className="tab-bar">
                        {Object.keys(committedGroupedResults).map(groupKey => (
                            <button
                                key={groupKey}
                                className={selectedTab === groupKey ? 'active-tab' : ''}
                                onClick={() => setSelectedTab(groupKey)}
                            >
                                {groupKey} ({committedGroupedResults[groupKey].length})
                            </button>
                        ))}
                    </div>
                </div>
            )}
            {!viewingLive && (
                <div style={{ marginBottom: 8 }}>
                    <span style={{ fontSize: 12, color: '#607d8b' }}>
                        📂 {harSessions[activeSessionKey]?.title || 'HAR session'}
                    </span>
                </div>
            )}

            {/* Scope the activity monitor to the selected platform tab. Feed it the
                RAW group entries (currentGroup) — they carry `.timestamp`/`.requestType`,
                which the monitor reads. displayRows are toNetworkRow-transformed (timestamp
                → startedAt), so passing those blanks the 60s window.
                WatchPanel (docs/signals-recommendations-watch-spec.md §6) sits at the
                strip's top-right corner — the "lighter touch" bell/badge over a full
                docked panel, wired straight to TrafficContext's watch-alert ring. */}
            <div style={{ position: 'relative' }}>
                <MemoStreamActivityMonitor rows={currentGroup} onTypeClick={setTypeFilter} activeType={typeFilter} />
                <div style={{ position: 'absolute', top: 6, right: 8, zIndex: 30 }}>
                    <WatchPanel alerts={watchAlerts} onDismiss={dismissWatchAlert} onClear={clearWatchAlerts} />
                </div>
            </div>

            {/* Collapsed-earlier-session banner — only when a boundary is active AND it
                actually hid something. Toggling showEarlier flows the unfiltered rows
                straight through to InspectraNetworkView (visit groups reappear as-is). */}
            {effectiveBoundary && earlierSessionRows.length > 0 && (
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: 8, padding: '6px 12px', marginBottom: 8,
                    background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 8,
                    fontSize: 12, color: 'var(--muted)',
                }}>
                    <span>
                        {earlierSessionRows.length} earlier request{earlierSessionRows.length === 1 ? '' : 's'} — before session start {new Date(effectiveBoundary).toTimeString().slice(0, 5)}
                    </span>
                    <button
                        onClick={() => setShowEarlier(v => !v)}
                        style={{
                            fontSize: 12, padding: '3px 10px', borderRadius: 6,
                            border: '1px solid var(--line)', background: 'transparent',
                            color: 'var(--ink)', cursor: 'pointer', fontWeight: 600,
                        }}
                    >
                        {showEarlier ? 'Hide' : 'Show'}
                    </button>
                </div>
            )}

            <MemoInspectraNetworkView
                data={displayRows}
                typeFilter={typeFilter}
                onTypeFilterChange={setTypeFilter}
                serverIp={serverIp}
                tlsDrops={tlsDropsArray}
                readonly={!viewingLive}
                paused={!!pausedDevices[selectedTab]}
                suppressed={suppressedByDevice[selectedTab] || 0}
                onTogglePause={handleTogglePause}
                configAttributesGlobal={configAttributesGlobal}
                publisherSettings={publisherSettingsForView}
                onClear={viewingLive ? handleClearLive : undefined}
                // onClearAll removed 2026-08-11 — New session now performs the same
                // unscoped clear (TrafficContext.startNewSession), including the
                // belt-and-suspenders local reset that guarded the "phantom device
                // count" bug. The scoped onClear above is unaffected.
                onRowOpen={handleRowOpen}
                allDevicesData={allDevicesRows}
                currentDevice={effectiveTab}
                onSelectDevice={setSelectedTab}
                slotLink={slotLink}
                onSlotLinkDone={handleSlotLinkDone}
                harId={viewingLive ? null : activeSessionKey}
            />
        </div>
    );
}

export default MainApp;
